import { ReactElement, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y } from "swiper/modules";
import type { Swiper as SwiperCore } from "swiper";
import "swiper/css";
import { pauseCircleOutline, volumeHighOutline } from "ionicons/icons";
import ContentRenderer from "../../../ContentRenderer/ContentRenderer";
import NextPrevButtons from "../../../../molecules/NextPrevButtons";
import Container from "../../../../atoms/Container";
import Icon from "../../../../atoms/Icon";
import useVocalisation from "../../../../../hooks/useVocalisation";
import { useConfig } from "../../../../../contexts/ConfigContext";
import { RichContentData } from "../../../../../types/richContent";
import { toPlainText } from "../../../../../helper/richContentText";

interface SwiperOnboardingProps {
  /** The researcher-authored ONBOARDING-STUDY content. */
  content: RichContentData;
  /** Leave the onboarding step forward (into inclusion/inscription). */
  onFinish: () => void;
  /** Go back to the previous welcome step (from the first slide). */
  onPrevious: () => void;
}

/**
 * Splits one authored document into pages on `delimiter` (* * *) blocks — each
 * page becomes a swiper slide. Empty segments (leading/trailing/double
 * delimiters) are dropped; with no delimiter the whole document is one page.
 */
const splitIntoPages = (content: RichContentData): RichContentData[] => {
  const pages: RichContentData["blocks"][] = [[]];
  (content.blocks ?? []).forEach((block) => {
    if (block.type === "delimiter") {
      pages.push([]);
    } else {
      pages[pages.length - 1].push(block);
    }
  });
  const nonEmpty = pages
    .filter((blocks) => blocks.length > 0)
    .map((blocks) => ({ ...content, blocks }));
  // Never return zero pages: an all-delimiter/empty document still gets one slide.
  return nonEmpty.length ? nonEmpty : [{ ...content, blocks: [] }];
};

const SwiperOnboarding = ({
  content,
  onFinish,
  onPrevious,
}: SwiperOnboardingProps): ReactElement => {
  const { t } = useTranslation();
  const { selectedRole } = useConfig();
  const { readText, isReading, stopReading } = useVocalisation();

  const pages = useMemo(() => splitIntoPages(content), [content]);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperCore | null>(null);

  const isFirst = activeIndex <= 0;
  const isLast = activeIndex >= pages.length - 1;

  // Vocalisation is on unless the role explicitly disables it (mirrors DisplayContent).
  const isVocalisationEnabled =
    selectedRole?.attributes?.is_vocalisation_enabled !== false;

  const goNext = () => {
    if (isLast) onFinish();
    else swiperRef.current?.slideNext();
  };

  const goPrev = () => {
    if (isFirst) onPrevious();
    else swiperRef.current?.slidePrev();
  };

  const handleRead = () => readText(toPlainText(pages[activeIndex]));

  return (
    <Container>
      {/* Read-aloud control in its own row (not overlaid on the slide, so it
          never covers the first block's text). Reads the active slide. */}
      {isVocalisationEnabled && (
        <TopBar>
          {isReading ? (
            <IconButton icon={pauseCircleOutline} onClick={stopReading} />
          ) : (
            <IconButton icon={volumeHighOutline} onClick={handleRead} />
          )}
        </TopBar>
      )}

      <SwiperArea>
        {/* Horizontal pager runs LTR for every language — the app has no
            app-wide RTL/dir handling, so this matches the rest of the UI. */}
        <Swiper
          modules={[A11y]}
          slidesPerView={1}
          onSwiper={(s) => (swiperRef.current = s)}
          onSlideChange={(s) => {
            // Reading follows the eye: stop TTS when the slide changes.
            if (isReading) stopReading();
            setActiveIndex(s.activeIndex);
          }}
          style={{ position: "absolute", inset: 0 }}
        >
          {pages.map((page, i) => (
            <SwiperSlide key={i}>
              <SlideScroll>
                <ContentRenderer data={page} />
              </SlideScroll>
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperArea>

      {pages.length > 1 && (
        <Dots role="group" aria-label={t("onboarding_screens_label")}>
          {pages.map((_, i) => (
            <Dot
              key={i}
              type="button"
              $active={i === activeIndex}
              aria-current={i === activeIndex ? "true" : undefined}
              aria-label={t("onboarding_go_to_screen", { number: i + 1 })}
              onClick={() => swiperRef.current?.slideTo(i)}
            />
          ))}
        </Dots>
      )}

      <NextPrevButtons
        onPreviousClick={goPrev}
        onNextClick={goNext}
        nextText={isLast ? t("common_start") : t("common_next")}
      />
    </Container>
  );
};

const SwiperArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`;

const SlideScroll = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 8px 20px 16px;
  -webkit-overflow-scrolling: touch;
  /* Breakout contract for full-bleed blocks (hero width='full') — matches the
     padding above so a cover slide reaches the slide edges. */
  --content-inset: 20px;
  --content-inset-top: 8px;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: 12px 0 4px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${(p) => (p.$active ? "22px" : "8px")};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: ${(p) =>
    p.$active ? p.theme.primary.main : "rgba(0, 0, 0, 0.18)"};
  transition: width 0.2s ease, background 0.2s ease;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 4px 20px 0;
`;

const IconButton = styled(Icon)`
  font-size: 26px;
  color: ${(props) => props.theme.primary.main};
  &:active {
    opacity: 0.5;
  }
`;

export default SwiperOnboarding;
